import React, { useEffect, useMemo, useState } from 'react';
import { ListOrdered, Trash2, CheckSquare, Plus, SquareDashed, GitFork } from 'lucide-react';
import { useEditorStore } from '../../store/editorStore';
import { useShallow } from 'zustand/react/shallow';
import { useConfirmStore } from '../../store/confirmStore';
import { MASK_PRESETS, previewMask } from '../../utils/naming';
import { FIELD_STYLE, ROW, LABEL, SECTION } from './PropertiesEditorStyles';
import { WhatIfPanel } from './WhatIfPanel';
import { buildRenumberPlan } from '../../utils/renumberPreview.logic';
import { previewBoundsExpansion } from '../../utils/floorPlanGeometry.logic';
import api from '../../utils/api';
import { RowExtensionModal } from './RowExtensionModal';
import { RowSlotModal } from './RowSlotModal';
import { RowOperationsModal } from './RowOperationsModal';
import { NamingProfileEditor } from './NamingProfileEditor';

type ModuleOption = { id: string; code: string; name: string };

export const DcInspector: React.FC<{ dcCode?: string }> = ({ dcCode }) => {
  const {
    racks, updateRack, rows, updateRow, deleteRow, dcSettings, setDcSettings, setSelectedIds, selectedRowId, setSelectedRowId
  } = useEditorStore(useShallow(state => ({
    racks: state.racks, updateRack: state.updateRack, rows: state.rows, updateRow: state.updateRow, 
    deleteRow: state.deleteRow, dcSettings: state.dcSettings, setDcSettings: state.setDcSettings, 
    setSelectedIds: state.setSelectedIds, selectedRowId: state.selectedRowId, setSelectedRowId: state.setSelectedRowId
  })));

  const [usingCustomMask, setUsingCustomMask] = useState(false);
  const [extensionRowId, setExtensionRowId] = useState<string | null>(null);
  const [slotRowId, setSlotRowId] = useState<string | null>(null);
  const [operationsRowId, setOperationsRowId] = useState<string | null>(null);
  const [modules, setModules] = useState<ModuleOption[]>([]);
  useEffect(() => {
    if (!dcCode) return;
    api.get(`/api/datacenters/${dcCode}/hierarchy`).then(({ data }) => {
      setModules((data || []).flatMap((hall: any) => hall.modules || []));
    }).catch(() => setModules([]));
  }, [dcCode]);
  const mask = dcSettings.namingMask;
  const preview = previewMask(mask, dcSettings.module, dcSettings.hall);
  const floorPlan = dcSettings.floorPlan;
  const [floorPlanDraft, setFloorPlanDraft] = useState(floorPlan);
  useEffect(() => setFloorPlanDraft(floorPlan), [floorPlan]);
  const updateFloorPlan = (key: keyof typeof floorPlan, value: number) => {
    const positive = key === 'tileSizeMm' || key === 'widthMm' || key === 'heightMm';
    setFloorPlanDraft({ ...floorPlanDraft, [key]: positive ? Math.max(1, value) : value });
  };
  const boundsPreview = useMemo(() => {
    try {
      return previewBoundsExpansion(floorPlan, floorPlanDraft);
    } catch {
      return null;
    }
  }, [floorPlan, floorPlanDraft]);
  const renumberPlan = useMemo(() => buildRenumberPlan({
    racks,
    rows,
    mask,
    module: dcSettings.module,
    hall: dcSettings.hall,
    namingProfiles: dcSettings.namingProfiles,
    namingAssignments: dcSettings.namingAssignments,
  }), [racks, rows, mask, dcSettings.module, dcSettings.hall, dcSettings.namingProfiles, dcSettings.namingAssignments]);
  const renumberConflicts = useMemo(
    () => [...new Set(renumberPlan.flatMap(item => item.conflicts))],
    [renumberPlan],
  );

  const handleRenumber = () => {
    if (renumberConflicts.length > 0) return;
    renumberPlan.forEach(item => {
      updateRack(item.rackId, {
        label: item.newAddress,
        rackPosInRow: item.rackPosInRow,
        rowIndex: item.rowIndex,
        rackIndex: item.rackIndex,
        isDirty: true,
      });
    });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {dcCode && <WhatIfPanel dcCode={dcCode} />}
      <div style={{ padding: '12px 14px', fontSize: 12, overflowY: 'auto' }}>
        <div style={{ fontWeight: 600, color: '#9ca3af', marginBottom: 12, fontSize: 12 }}>
          Настройки ЦОД
        </div>
        <div style={SECTION}>Схема нумерации стоек</div>
        <div style={{ ...ROW, marginBottom: 6 }}>
          <label style={LABEL}>Шаблон маски</label>
        <select
          style={{ ...FIELD_STYLE, cursor: 'pointer' }}
          value={usingCustomMask ? 'custom' : mask}
          onChange={e => {
            if (e.target.value === 'custom') {
              setUsingCustomMask(true);
            } else {
              setUsingCustomMask(false);
              setDcSettings({ namingMask: e.target.value });
            }
          }}
        >
          {MASK_PRESETS.map(p => (
            <option key={p.mask} value={p.mask}>{p.label}</option>
          ))}
          <option value="custom">Своя маска...</option>
        </select>
      </div>
      {usingCustomMask && (
        <div style={{ ...ROW, marginBottom: 6 }}>
          <label style={LABEL}>Своя маска</label>
          <input
            style={FIELD_STYLE}
            value={mask}
            onChange={e => setDcSettings({ namingMask: e.target.value })}
            placeholder="{module}-{row:02d}-{rack:02d}"
          />
        </div>
      )}
      <NamingProfileEditor
        profiles={dcSettings.namingProfiles}
        assignments={dcSettings.namingAssignments}
        modules={modules.map(module => ({ id: module.id, label: `${module.code} · ${module.name}` }))}
        rows={Object.values(rows).map(row => ({ id: row.id, label: `${row.labelIndex}. ${row.name}` }))}
        onChange={patch => setDcSettings(patch)}
      />
      <div style={{ ...ROW, marginBottom: 8 }}>
        <label style={LABEL}>Переменные маски</label>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 4 }}>
          <input style={FIELD_STYLE} value={dcSettings.module}
            onChange={e => setDcSettings({ module: e.target.value })}
            placeholder="Модуль: 01" />
          <input style={FIELD_STYLE} value={dcSettings.hall}
            onChange={e => setDcSettings({ hall: e.target.value })}
            placeholder="Зал: A" />
        </div>
        <div style={{ fontSize: 10, color: '#4b5563', marginTop: 3 }}>
          Пример: <span style={{ color: '#60a5fa', fontFamily: 'monospace' }}>{preview}</span>
        </div>
      </div>
      <button onClick={handleRenumber} style={{
        width: '100%', padding: '6px 8px', marginBottom: 6,
        background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.3)',
        borderRadius: 4, color: '#60a5fa', fontSize: 11, cursor: renumberConflicts.length ? 'not-allowed' : 'pointer',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
      }} disabled={renumberConflicts.length > 0}>
        <ListOrdered size={13} /> Перенумеровать (черновик)
      </button>
      {renumberConflicts.length > 0 && (
        <div role="alert" style={{ color: '#fca5a5', fontSize: 10, marginBottom: 8 }}>
          {renumberConflicts.slice(0, 3).map(conflict => <div key={conflict}>{conflict}</div>)}
          {renumberConflicts.length > 3 && <div>И ещё {renumberConflicts.length - 3} конфликтов.</div>}
        </div>
      )}
      {renumberPlan.length > 0 && (
        <div style={{ border: '1px solid #1f2937', padding: 8, marginBottom: 8, fontSize: 10 }}>
          <div style={{ color: '#9ca3af', marginBottom: 5 }}>Предпросмотр адресов</div>
          {renumberPlan.slice(0, 8).map(item => (
            <div key={item.rackId} style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontFamily: 'monospace' }}>
              <span style={{ color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.oldAddress}</span>
              <span style={{ color: '#60a5fa' }}>→ {item.newAddress}</span>
            </div>
          ))}
          {renumberPlan.length > 8 && <div style={{ color: '#6b7280', marginTop: 4 }}>и ещё {renumberPlan.length - 8}</div>}
        </div>
      )}
      <div style={{ height: 1, background: '#1f2937', marginBottom: 12 }} />
      <div style={SECTION}>Геометрия плана</div>
      <div style={{ fontSize: 10, color: '#6b7280', marginBottom: 8 }}>
        Метрические значения. Изменение сетки не пересчитывает размещённые объекты.
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
        {([
          ['originXMm', 'Начало X (мм)'],
          ['originYMm', 'Начало Y (мм)'],
          ['widthMm', 'Ширина (мм)'],
          ['heightMm', 'Высота (мм)'],
          ['tileSizeMm', 'Плитка (мм)'],
        ] as const).map(([key, label]) => (
          <div style={ROW} key={key}>
            <label style={LABEL}>{label}</label>
            <input
              style={FIELD_STYLE}
              type="number"
              min={key === 'tileSizeMm' || key === 'widthMm' || key === 'heightMm' ? 1 : undefined}
              step={1}
              value={floorPlanDraft[key]}
              onChange={e => updateFloorPlan(key, Math.round(Number(e.target.value)))}
            />
          </div>
        ))}
      </div>
      {boundsPreview && (
        <div style={{ fontSize: 10, color: boundsPreview.expanded ? '#86efac' : '#fbbf24', marginBottom: 8 }}>
          {boundsPreview.expanded ? 'План расширяется, размещение объектов сохраняется.' : 'План сужается или смещается внутрь: проверьте стойки перед применением.'}
        </div>
      )}
      <button
        onClick={() => setDcSettings({ floorPlan: floorPlanDraft })}
        disabled={!boundsPreview?.expanded}
        style={{ ...FIELD_STYLE, display: 'inline-flex', alignItems: 'center', gap: 6, cursor: boundsPreview?.expanded ? 'pointer' : 'not-allowed', color: '#d1d5db', marginBottom: 8 }}
      >
        <CheckSquare size={13} /> Применить границы
      </button>
      <div style={{ fontSize: 10, color: '#4b5563', marginBottom: 10 }}>
        Границы: {floorPlanDraft.originXMm}…{floorPlanDraft.originXMm + floorPlanDraft.widthMm} мм × {floorPlanDraft.originYMm}…{floorPlanDraft.originYMm + floorPlanDraft.heightMm} мм
      </div>
      <div style={{ height: 1, background: '#1f2937', marginBottom: 12 }} />
      <div style={SECTION}>Размеры по умолчанию</div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
        <div style={ROW}>
          <label style={LABEL}>Ширина (м)</label>
          <input style={FIELD_STYLE} type="number" step={0.05} value={dcSettings.defaultRackWidthM}
            onChange={e => setDcSettings({ defaultRackWidthM: parseFloat(e.target.value) })} />
        </div>
        <div style={ROW}>
          <label style={LABEL}>Глубина (м)</label>
          <input style={FIELD_STYLE} type="number" step={0.05} value={dcSettings.defaultRackDepthM}
            onChange={e => setDcSettings({ defaultRackDepthM: parseFloat(e.target.value) })} />
        </div>
        <div style={ROW}>
          <label style={LABEL}>Высота (м)</label>
          <input style={FIELD_STYLE} type="number" step={0.1} value={dcSettings.defaultRackHeightM}
            onChange={e => setDcSettings({ defaultRackHeightM: parseFloat(e.target.value) })} />
        </div>
        <div style={ROW}>
          <label style={LABEL}>Лоток над стойкой (м)</label>
          <input style={FIELD_STYLE} type="number" step={0.05} value={dcSettings.defaultTrayHeightM}
            onChange={e => setDcSettings({ defaultTrayHeightM: parseFloat(e.target.value) })} />
        </div>
        <div style={ROW}>
          <label style={LABEL}>Ширина коридора (м)</label>
          <input style={FIELD_STYLE} type="number" step={0.1} value={dcSettings.defaultAisleWidthM}
            onChange={e => setDcSettings({ defaultAisleWidthM: parseFloat(e.target.value) })} />
        </div>
      </div>
      <div style={{ height: 1, background: '#1f2937', marginBottom: 12 }} />
      <div style={SECTION}>Ряды</div>
      {Object.values(rows).length === 0 && (
        <div style={{ fontSize: 11, color: '#4b5563', fontStyle: 'italic', marginBottom: 8 }}>
          Нет рядов. Создайте через Матричный генератор.
        </div>
      )}
      {Object.values(rows).sort((a, b) => a.labelIndex - b.labelIndex).map(row => {
        const rackCount = Object.values(racks).filter(r => r.rowId === row.id).length;
        return (
          <div key={row.id} style={{
            background: selectedRowId === row.id ? 'rgba(14, 165, 233, 0.08)' : '#111827',
            border: selectedRowId === row.id ? '1px solid rgba(14, 165, 233, 0.45)' : '1px solid #1f2937', borderRadius: 4,
            padding: '6px 8px', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8,
          }}>
            <div style={{ width: 10, height: 10, borderRadius: 2, background: row.color ?? 'var(--status-unknown)', flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <input
                style={{ ...FIELD_STYLE, padding: '1px 4px', width: '100%' }}
                value={row.name}
                onChange={e => updateRow(row.id, { name: e.target.value })}
              />
            </div>
            <select
              aria-label={`Модуль ряда ${row.name}`}
              style={{ ...FIELD_STYLE, width: 92, padding: '2px 4px', fontSize: 10 }}
              value={row.moduleId || ''}
              onChange={e => updateRow(row.id, { moduleId: e.target.value || null })}
              title="Модуль ряда"
            >
              <option value="">Без модуля</option>
              {modules.map(module => (
                <option key={module.id} value={module.id}>{module.code} · {module.name}</option>
              ))}
            </select>
            <div style={{ fontSize: 10, color: '#4b5563', flexShrink: 0 }}>{rackCount} ст.</div>
            <button
              onClick={() => {
                setSelectedRowId(row.id);
                setSelectedIds(Object.values(racks).filter(r => r.rowId === row.id).map(r => r.id));
              }}
              style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', padding: '0 2px', display: 'flex', alignItems: 'center' }}
              title="Выбрать ряд и все его стойки"
            ><CheckSquare size={12} /></button>
            <button
              onClick={() => setExtensionRowId(row.id)}
              style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', padding: '0 2px', display: 'flex', alignItems: 'center' }}
              title="Продлить ряд"
            ><Plus size={13} /></button>
            <button
              onClick={() => setSlotRowId(row.id)}
              style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', padding: '0 2px', display: 'flex', alignItems: 'center' }}
              title="Добавить место, резерв или In-Row Cooler"
            ><SquareDashed size={13} /></button>
            <button
              onClick={() => setOperationsRowId(row.id)}
              style={{ background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', padding: '0 2px', display: 'flex', alignItems: 'center' }}
              title="Операции ряда"
            ><GitFork size={13} /></button>
            <button
              onClick={async () => {
                const confirmed = await useConfirmStore.getState().confirm({
                  title: 'Удаление ряда стоек',
                  message: `Удалить ряд "${row.name}"?`,
                  confirmText: 'Удалить',
                  cancelText: 'Отмена'
                });
                if (confirmed) deleteRow(row.id);
              }}
              style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', padding: '0 2px', display: 'flex', alignItems: 'center' }}
              title="Удалить ряд"
            ><Trash2 size={12} /></button>
          </div>
        );
      })}
      </div>
      {extensionRowId && <RowExtensionModal rowId={extensionRowId} onClose={() => setExtensionRowId(null)} />}
      {slotRowId && <RowSlotModal rowId={slotRowId} onClose={() => setSlotRowId(null)} />}
      {operationsRowId && <RowOperationsModal rowId={operationsRowId} onClose={() => setOperationsRowId(null)} />}
    </div>
  );
};
